/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { MigrationApiService } from './migration-api.service';
import { MigrationService } from './migration.service';
import { MigrationError, MigrationRun } from './migration.models';

interface ErrorRow extends MigrationError {
  jobId: string;
  runNodeId?: string;
}

/**
 * Errors & Retry — the failed items from every run report the engine wrote.
 * Retry targets single items, not the whole job, and needs the engine: with
 * no service the actions are disabled, not faked.
 */
@Component({
  selector: 'app-mig-errors',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-errors.component.html'
})
export class MigrationErrorsComponent implements OnInit {
  private readonly service = inject(MigrationService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);
  readonly api = inject(MigrationApiService);

  readonly loading = signal(true);
  readonly rows = signal<ErrorRow[]>([]);
  readonly busyKey = signal('');
  readonly page = signal(0);
  readonly pageSize = 50;

  ngOnInit(): void {
    // The engine keeps Runs/index.json — slim per-run summaries with an error
    // count, so only reports that actually contain errors get downloaded.
    // Engines that predate the index have no index.json — fall back to the scan.
    this.service.runIndex().subscribe((index) => {
      if (index.length) {
        const withErrors = index.filter((e) => (e.errors ?? 0) > 0 && e.nodeId);
        if (!withErrors.length) {
          this.loading.set(false);
          return;
        }
        forkJoin(withErrors.map((e) => this.service.report(e.nodeId))).subscribe((reports) => this.setRows(reports));
      } else {
        this.service.runs(200).subscribe((runs) => this.setRows(runs));
      }
    });
  }

  private setRows(reports: (MigrationRun | null)[]): void {
    const rows: ErrorRow[] = [];
    for (const run of reports) {
      if (!run) {
        continue;
      }
      for (const err of run.errors ?? []) {
        rows.push({ ...err, jobId: run.jobId, runNodeId: run.nodeId });
      }
    }
    this.rows.set(rows);
    this.loading.set(false);
  }

  paged(): ErrorRow[] {
    return this.rows().slice(this.page() * this.pageSize, (this.page() + 1) * this.pageSize);
  }

  pageCount(): number {
    return Math.max(1, Math.ceil(this.rows().length / this.pageSize));
  }

  pageRange(): { from: number; to: number; total: number } {
    const total = this.rows().length;
    const from = total ? this.page() * this.pageSize + 1 : 0;
    return { from, to: Math.min(total, from + this.pageSize - 1), total };
  }

  setPage(delta: number): void {
    this.page.set(Math.min(this.pageCount() - 1, Math.max(0, this.page() + delta)));
  }

  retry(row: ErrorRow): void {
    if (this.api.available() !== true || !row.jobId) {
      return;
    }
    this.busyKey.set(row.item);
    this.api.retry(row.jobId, [row.item]).subscribe({
      next: () => {
        this.busyKey.set('');
        this.service.audit('migration.retry', { jobId: row.jobId, detail: row.item }).subscribe({ error: () => undefined });
        this.notification.showInfo(this.translate.instant('DMS.MIGRATION.RETRY_SENT'));
      },
      error: (e) => {
        this.busyKey.set('');
        this.notification.showError(e?.error?.detail ?? this.translate.instant('DMS.MIGRATION.CTL_FAIL'));
      }
    });
  }

  dateLabel(iso?: string): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : (this.datePipe.transform(d, 'dd/MM/yyyy HH:mm') ?? '—');
  }

  trackByRow(_i: number, r: ErrorRow): string {
    return `${r.jobId}-${r.item}`;
  }
}
